import React, { useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { cn } from "../../lib/utils";
import { SEARCH_PLACEHOLDER_ROTATE_MS } from "../../lib/searchPlaceholders";

/**
 * Overlay that cycles placeholder copy while the wrapped field is empty.
 *
 * @param {Object} props
 * @param {string[]} props.placeholders
 * @param {boolean} props.isEmpty
 * @param {boolean} [props.enabled=true]
 * @param {string} [props.className]
 * @param {string} [props.overlayClassName]
 * @param {React.ReactNode} props.children
 * @returns {JSX.Element}
 */
export function CyclingPlaceholderField({
  placeholders,
  isEmpty,
  enabled = true,
  className,
  overlayClassName,
  children,
}) {
  const reduceMotion = useReducedMotion();
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const list = Array.isArray(placeholders) && placeholders.length > 0
    ? placeholders
    : [""];
  const placeholder = list[placeholderIndex % list.length];

  useEffect(() => {
    setPlaceholderIndex(0);
  }, [placeholders]);

  useEffect(() => {
    if (!enabled || !isEmpty || list.length < 2) return undefined;
    const id = window.setInterval(() => {
      setPlaceholderIndex((i) => (i + 1) % list.length);
    }, SEARCH_PLACEHOLDER_ROTATE_MS);
    return () => window.clearInterval(id);
  }, [enabled, isEmpty, list.length]);

  const transition = reduceMotion
    ? { duration: 0 }
    : { duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] };

  return (
    <div className={cn("relative min-w-0 flex-1 overflow-hidden", className)}>
      {isEmpty ? (
        <div
          className={cn(
            "pointer-events-none absolute inset-0 flex items-center overflow-hidden",
            overlayClassName
          )}
          aria-hidden
        >
          <AnimatePresence mode="wait" initial={false}>
            <motion.span
              key={placeholder}
              initial={
                reduceMotion
                  ? { opacity: 1, y: 0 }
                  : { opacity: 0, y: "40%" }
              }
              animate={{ opacity: 1, y: 0 }}
              exit={
                reduceMotion
                  ? { opacity: 0, y: 0 }
                  : { opacity: 0, y: "-40%" }
              }
              transition={transition}
              className="truncate text-ui-muted-foreground"
            >
              {placeholder}
            </motion.span>
          </AnimatePresence>
        </div>
      ) : null}
      {children}
    </div>
  );
}
